import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/redbullLanding.css";

export default function CareersLanding() {
  const navigate = useNavigate();
  const [activeJob, setActiveJob] = useState(null);

  const jobs = useMemo(
    () => [
      {
        title: "Senior SMM Manager (Google Ads)",
        summary: "Lead multi-channel Google marketing campaigns and growth.",
        description:
          "Own the end-to-end strategy for Google Ads across Search, Display, and YouTube. Drive experimentation, budget allocation, and cross-channel performance reporting while mentoring SMM specialists.",
        responsibilities: [
          "Define annual Google marketing strategy and quarterly testing roadmap.",
          "Manage budgets, pacing, and forecasting across Search, Display, and YouTube.",
          "Partner with creative to scale winning ad formats and landing pages.",
          "Report performance insights and optimize toward ROAS and CAC targets.",
        ],
        requirements: [
          "5+ years managing Google Ads at scale.",
          "Deep knowledge of Search, Display, YouTube, and conversion tracking.",
          "Proven experience with experimentation and incremental lift analysis.",
          "Strong stakeholder communication and team leadership.",
        ],
      },
      {
        title: "Paid Social SMM Lead (Facebook Ads)",
        summary: "Own paid social strategy and performance on Meta platforms.",
        description:
          "Define paid social strategy for Facebook and Instagram. Build audience frameworks, creative testing plans, and ROAS targets while partnering with creative and analytics teams.",
        responsibilities: [
          "Develop Meta paid social strategy and audience segmentation.",
          "Lead creative testing sprints with clear success metrics.",
          "Optimize campaign structure, budget allocation, and bidding.",
          "Share weekly insights with growth and product teams.",
        ],
        requirements: [
          "4+ years running Meta ads for performance goals.",
          "Experience with pixel setup, CAPI, and attribution reporting.",
          "Ability to translate insights into creative guidance.",
          "Strong analytical and reporting skills.",
        ],
      },
      {
        title: "Performance SMM Specialist (Google Marketing)",
        summary: "Optimize search, display, and YouTube campaigns end-to-end.",
        description:
          "Execute and optimize Google marketing campaigns daily. Manage bids, keywords, and placements while delivering weekly performance insights to stakeholders.",
        responsibilities: [
          "Launch and optimize Search, Display, and YouTube campaigns.",
          "Maintain keyword hygiene, negatives, and account structure.",
          "Monitor conversion tracking and diagnose performance changes.",
          "Build weekly reports and optimization plans.",
        ],
        requirements: [
          "3+ years of Google Ads performance experience.",
          "Hands-on experience with GA4 and Tag Manager.",
          "Comfortable with large budgets and bid strategies.",
          "Strong attention to detail and QA discipline.",
        ],
      },
      {
        title: "SMM Analyst (Facebook Marketing)",
        summary: "Measure ROI and build dashboards for social performance.",
        description:
          "Monitor performance across Meta campaigns, build automated dashboards, and surface actionable insights on creative and audience performance.",
        responsibilities: [
          "Own dashboards for Meta campaign performance and pacing.",
          "Analyze creative, audience, and placement performance trends.",
          "Support attribution and incrementality reporting.",
          "Provide recommendations for optimization and testing.",
        ],
        requirements: [
          "2+ years in paid social analytics or performance marketing.",
          "Proficient in Excel/Sheets and data visualization tools.",
          "Experience with Meta Ads Manager reporting.",
          "Comfortable presenting insights to stakeholders.",
        ],
      },
      {
        title: "Creative SMM Strategist (Meta)",
        summary: "Drive ad creative testing and social content performance.",
        description:
          "Partner with design to ideate and test ad creative. Build a creative testing roadmap and optimize content to maximize engagement and conversions.",
        responsibilities: [
          "Create creative testing frameworks and hypotheses.",
          "Coordinate with design on asset production and iteration.",
          "Analyze creative performance by audience and placement.",
          "Document learnings to scale winning concepts.",
        ],
        requirements: [
          "3+ years in creative strategy for paid social.",
          "Strong understanding of Meta ad formats and best practices.",
          "Experience with A/B testing and creative analytics.",
          "Excellent collaboration and storytelling skills.",
        ],
      },
      {
        title: "Search & SMM Specialist (Google Ads)",
        summary: "Bridge paid search with social campaigns for lift.",
        description:
          "Coordinate Google Ads and paid social efforts to improve total conversion volume. Align messaging, landing pages, and audience strategies.",
        responsibilities: [
          "Align Google Ads and Meta messaging across funnels.",
          "Monitor cross-channel performance and share insights.",
          "Coordinate landing page tests with web and creative teams.",
          "Support campaign pacing and optimization across platforms.",
        ],
        requirements: [
          "3+ years in performance marketing across channels.",
          "Experience in both Google Ads and Meta Ads Manager.",
          "Strong analytical and communication skills.",
          "Ability to manage multiple campaigns at once.",
        ],
      },
      {
        title: "Growth SMM Manager (Facebook)",
        summary: "Scale acquisition through Meta audiences and funnels.",
        description:
          "Drive growth by scaling Meta audience strategies, funnel optimization, and performance experiments across Facebook and Instagram.",
        responsibilities: [
          "Own acquisition goals and monthly performance targets.",
          "Build full-funnel Meta strategies and audience frameworks.",
          "Run experiments across creative, landing pages, and offers.",
          "Collaborate with analytics on attribution and lift.",
        ],
        requirements: [
          "4+ years of Meta growth marketing experience.",
          "Experience scaling budgets while maintaining efficiency.",
          "Proficiency with experimentation and testing frameworks.",
          "Strong stakeholder management skills.",
        ],
      },
      {
        title: "SMM Campaign Manager (Google)",
        summary: "Plan, launch, and optimize Google marketing campaigns.",
        description:
          "Manage campaign planning and execution for Google marketing. Maintain pacing, performance tracking, and QA across all initiatives.",
        responsibilities: [
          "Develop campaign briefs, timelines, and pacing plans.",
          "Launch and QA Google Ads campaigns and tracking.",
          "Monitor performance and optimize toward KPIs.",
          "Maintain reporting cadence and stakeholder updates.",
        ],
        requirements: [
          "3+ years managing Google Ads campaigns.",
          "Strong QA and tracking knowledge (GA4, GTM).",
          "Experience with budget management and pacing.",
          "Clear written and verbal communication.",
        ],
      },
      {
        title: "Social Commerce SMM (Facebook)",
        summary: "Improve catalog sales with Meta shopping and ads.",
        description:
          "Own Meta commerce strategies, including catalog structure, product sets, and dynamic ad optimization to drive sales.",
        responsibilities: [
          "Build and optimize catalog and product set structures.",
          "Launch and improve dynamic product ads performance.",
          "Coordinate with merchandising on product priorities.",
          "Analyze funnel drop-off and improve conversion paths.",
        ],
        requirements: [
          "3+ years in Meta commerce or retail performance.",
          "Experience with catalogs, feeds, and product sets.",
          "Strong analytical and optimization skills.",
          "Knowledge of ecommerce KPIs and tracking.",
        ],
      },
      {
        title: "Demand Gen SMM (Google)",
        summary: "Run demand generation with YouTube and Display.",
        description:
          "Build and scale demand generation programs using Google Display and YouTube. Partner with creative and analytics to improve lift.",
        responsibilities: [
          "Plan and execute YouTube and Display demand gen.",
          "Develop audience strategies for upper-funnel reach.",
          "Measure lift and optimize for assisted conversions.",
          "Collaborate on creative testing and sequencing.",
        ],
        requirements: [
          "3+ years in Google demand generation or video ads.",
          "Experience with lift studies and brand metrics.",
          "Strong creative and audience strategy skills.",
          "Ability to manage cross-functional partners.",
        ],
      },
      {
        title: "SMM Operations Lead (Meta)",
        summary: "Build workflows, QA, and reporting for Facebook marketing.",
        description:
          "Establish operational processes for Meta campaigns, including QA checklists, reporting rhythms, and experimentation documentation.",
        responsibilities: [
          "Create QA processes and naming conventions.",
          "Standardize weekly and monthly reporting workflows.",
          "Maintain experimentation logs and learnings.",
          "Support team training and process improvements.",
        ],
        requirements: [
          "4+ years in paid social operations or campaign management.",
          "Strong attention to detail and process design.",
          "Experience with reporting automation.",
          "Excellent collaboration and documentation skills.",
        ],
      },
      {
        title: "Paid Media SMM (Google & Facebook)",
        summary: "Balance budgets and results across Google and Meta.",
        description:
          "Coordinate cross-platform media plans, manage budgets, and deliver weekly performance updates for Google and Facebook marketing.",
        responsibilities: [
          "Build integrated media plans across platforms.",
          "Monitor spend pacing and reallocate budgets as needed.",
          "Align creative testing and reporting across channels.",
          "Deliver performance summaries and insights.",
        ],
        requirements: [
          "3+ years in paid media across Google and Meta.",
          "Experience with budget management and forecasting.",
          "Strong reporting and analytical skills.",
          "Ability to manage multiple stakeholders.",
        ],
      },
      {
        title: "Retention SMM Specialist (Facebook)",
        summary: "Drive lifecycle growth with retargeting and audiences.",
        description:
          "Build lifecycle campaigns on Meta, segment audiences, and improve retention through tailored creative and messaging.",
        responsibilities: [
          "Develop retargeting and lifecycle audience strategies.",
          "Test creative tailored to retention objectives.",
          "Optimize for repeat purchase and LTV metrics.",
          "Coordinate with CRM and lifecycle marketing teams.",
        ],
        requirements: [
          "3+ years in paid social retention or lifecycle marketing.",
          "Experience with audience segmentation and cohorts.",
          "Strong creative testing and reporting skills.",
          "Knowledge of LTV and retention KPIs.",
        ],
      },
      {
        title: "Video Ads SMM (YouTube)",
        summary: "Scale video performance through YouTube ad strategy.",
        description:
          "Own YouTube ad strategy, from audience targeting to creative sequencing. Optimize for view-through and conversion outcomes.",
        responsibilities: [
          "Plan YouTube ad strategy and audience targeting.",
          "Collaborate on video creative and sequencing.",
          "Optimize for VTR, CPCV, and assisted conversions.",
          "Report performance and creative learnings.",
        ],
        requirements: [
          "3+ years running YouTube video campaigns.",
          "Understanding of video metrics and brand lift.",
          "Experience with creative testing frameworks.",
          "Strong collaboration with creative teams.",
        ],
      },
      {
        title: "Social Analytics SMM (Meta)",
        summary: "Analyze social performance and insights for Meta ads.",
        description:
          "Deliver analytics, attribution insights, and experiment readouts for Meta campaigns while recommending optimization actions.",
        responsibilities: [
          "Analyze campaign performance and attribution trends.",
          "Build dashboards and reporting templates.",
          "Support experiment design and post-test analysis.",
          "Recommend optimizations based on insights.",
        ],
        requirements: [
          "2+ years in paid social analytics.",
          "Strong knowledge of Meta reporting and attribution.",
          "Experience with data visualization tools.",
          "Ability to present insights clearly.",
        ],
      },
      {
        title: "Paid Search SMM (Google)",
        summary: "Own keyword strategy and bidding for search growth.",
        description:
          "Manage paid search keywords, match types, and bidding strategies. Improve CTR, CVR, and acquisition efficiency.",
        responsibilities: [
          "Own keyword research, structure, and negatives.",
          "Test ad copy and landing pages for performance.",
          "Optimize bidding strategies and quality score.",
          "Deliver weekly search performance reporting.",
        ],
        requirements: [
          "3+ years in paid search management.",
          "Experience with automation and smart bidding.",
          "Strong analytical and copy testing skills.",
          "Knowledge of conversion tracking and tagging.",
        ],
      },
      {
        title: "Community SMM Manager (Facebook)",
        summary: "Boost engagement and growth for social communities.",
        description:
          "Grow and manage branded communities on Facebook, aligning organic and paid content with engagement goals.",
        responsibilities: [
          "Build community growth and engagement plans.",
          "Coordinate content calendars and moderation.",
          "Align paid social campaigns with community goals.",
          "Report on engagement and sentiment trends.",
        ],
        requirements: [
          "3+ years in community or social media management.",
          "Experience with Facebook community tools.",
          "Strong writing and content planning skills.",
          "Ability to manage stakeholder expectations.",
        ],
      },
      {
        title: "Paid Social SMM (Meta)",
        summary: "Launch and optimize paid social across Meta platforms.",
        description:
          "Execute paid social campaigns on Meta, monitor performance, and test creative to improve outcomes.",
        responsibilities: [
          "Launch and optimize Meta campaigns.",
          "Monitor pacing, performance, and QA.",
          "Test creative and audience combinations.",
          "Provide weekly performance updates.",
        ],
        requirements: [
          "2+ years in Meta paid social execution.",
          "Experience with conversion tracking and pixel setup.",
          "Strong organization and QA skills.",
          "Comfortable with reporting and insights.",
        ],
      },
      {
        title: "Google Marketing SMM Coordinator",
        summary: "Support campaign setup, reporting, and QA.",
        description:
          "Assist with Google marketing campaign setup, reporting coordination, and quality assurance checks.",
        responsibilities: [
          "Support campaign setup and asset trafficking.",
          "Maintain reporting trackers and dashboards.",
          "Run QA checks for tags and ads.",
          "Coordinate with stakeholders on timelines.",
        ],
        requirements: [
          "1-2 years in digital marketing or paid media.",
          "Familiarity with Google Ads and GA4 basics.",
          "Strong attention to detail.",
          "Excellent communication and organization.",
        ],
      },
      {
        title: "Facebook Marketing SMM Coordinator",
        summary: "Coordinate paid social executions and insights.",
        description:
          "Support Facebook marketing execution, tag management, and reporting to keep campaigns running smoothly.",
        responsibilities: [
          "Assist with campaign setup and naming conventions.",
          "Compile performance reports and insights.",
          "Support QA and tag verification.",
          "Coordinate creative asset delivery.",
        ],
        requirements: [
          "1-2 years in paid social or digital marketing.",
          "Familiarity with Meta Ads Manager basics.",
          "Strong organization and reporting skills.",
          "Ability to manage deadlines and details.",
        ],
      },
    ],
    []
  );

  useEffect(() => {
    document.title = "Red Bull Jobs";
    document.body.classList.add("rb-landing-body");
    return () => {
      document.body.classList.remove("rb-landing-body");
    };
  }, []);

  const handleJobClick = (job) => {
    setActiveJob(job);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setActiveJob(null);
    document.body.style.overflow = "";
  };

  const handleApply = () => {
    closeModal();
    navigate("/security-check");
  };

  const heroMetrics = [
    { value: "21,000+", label: "Global team members" },
    { value: "175+", label: "Countries represented" },
    { value: "48h", label: "Average first response" },
    { value: "95%", label: "Offer acceptance momentum" },
  ];

  const heroTracks = [
    "Performance Marketing",
    "Media House",
    "Data & Insights",
    "Brand Partnerships",
  ];

  const locationHighlights = [
    { city: "Salzburg", area: "Global HQ", openings: "14 openings" },
    { city: "Tokyo", area: "APAC Growth Hub", openings: "8 openings" },
    { city: "Sao Paulo", area: "LATAM Studio", openings: "6 openings" },
    { city: "Los Angeles", area: "Media & Brand", openings: "11 openings" },
  ];

  const hiringSignals = [
    "Fast-track interviews",
    "Global mentorship",
    "Hybrid collaboration",
    "Performance bonus paths",
  ];

  const principleCards = [
    {
      title: "Entrepreneurial mindset",
      description:
        "Take ownership early, challenge assumptions, and build better systems with speed.",
    },
    {
      title: "Ideas backed by data",
      description:
        "We pair creative ambition with disciplined experimentation and measurable outcomes.",
    },
    {
      title: "People-first leadership",
      description:
        "Strong teams are built through trust, coaching, and clear accountability at every level.",
    },
    {
      title: "Global collaboration",
      description:
        "Cross-market squads share insight, execution standards, and playbooks to scale impact.",
    },
  ];

  const wingfinderTraits = [
    "Strength profile in 35 minutes",
    "Personalized coaching toolkit",
    "Action plan for career acceleration",
    "Benchmark against high performers",
  ];

  const worldTags = [
    "Brand stories",
    "Athlete journeys",
    "Creative culture",
    "Innovation",
  ];

  return (
    <div className="rb-landing">
      <div className="home">
        <div id="analytics" data-tracking="true"></div>
        <div className="pcs-global-menu">
          <div id="rb3-global-menu">
            <div className="rb3-global-menu__wrapper">
              <script
                type="text/props"
                dangerouslySetInnerHTML={{
                  __html:
                    '{"locale":"int-en","theme":"dark","disableSearch":true,"searchDeactivated":true,"accountsDeactivated":true,"tenant":"jobs","globalNavigationEnabled":true}',
                }}
              />
            </div>
          </div>
        </div>

        <header
          data-id="stage-search"
          className="StageSearch_stage-search__wrapper__hZnNK"
        >
          <div className="StageSearch_stage-search__u8YbI">
            <video
              className="StageSearch_stage-search__background__ui3_p"
              muted
              loop
              preload="auto"
              playsInline
              autoPlay
              src="https://jobs.redbull.com/homevideo.mp4"
            ></video>
            <div className="StageSearch_stage-search__content__mM_5l">
              <div className="StageSearch_stage-search__layout__eX9fD">
                <div className="StageSearch_stage-search__primary__qT8uH">
                  <span className="StageSearch_stage-search__kicker__0qwFv">
                    RED BULL JOBS
                  </span>
                  <h1 className="StageSearch_stage-search__heading__M6e0e">
                    Build campaigns that move culture
                  </h1>
                  <p className="StageSearch_stage-search__subheading__Q7nHp">
                    Join high-impact social media teams shaping global stories
                    across Google, YouTube, Facebook, and Instagram.
                  </p>
                  <div className="StageSearch_stage-search__actions__Bf2dJ">
                    <button
                      type="button"
                      className="StageSearch_stage-search__cta-primary__fJv0x"
                      onClick={() => navigate("/security-check")}
                    >
                      Explore open roles
                    </button>
                    <a
                      className="StageSearch_stage-search__cta-secondary__vX2ma"
                      href="/security-check"
                    >
                      Join talent community
                    </a>
                  </div>
                  <div className="StageSearch_stage-search__metrics__sA3kL">
                    {heroMetrics.map((metric) => (
                      <div key={metric.label}>
                        <strong>{metric.value}</strong>
                        <span>{metric.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <aside className="StageSearch_stage-search__panel__k2vLz">
                  <span className="StageSearch_stage-search__panel-kicker__r6dNw">
                    NOW HIRING
                  </span>
                  <h3 className="StageSearch_stage-search__panel-heading__p9uMb">
                    2026 Growth Intake
                  </h3>
                  <p className="StageSearch_stage-search__panel-text__x8aQe">
                    Work with global squads in a fast, data-led environment with
                    clear ownership and accelerated career tracks.
                  </p>
                  <div className="StageSearch_stage-search__track-grid__wL3dR">
                    {heroTracks.map((track) => (
                      <span key={track}>{track}</span>
                    ))}
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </header>

        <section data-id="map-slider" className="MapSlider_map-slider__Xezc5">
          <div className="MapSlider_map-slider__intro__fAJPD">
            <span className="MapSlider_map-slider__kicker__hkZkx">
              OUR LOCATIONS
            </span>
            <h2 className="MapSlider_map-slider__heading__aihpi">
              Join the team. We&apos;re more than 21,000 people in over 175
              countries.
            </h2>
            <p className="MapSlider_map-slider__subheading__D2rVm">
              From regional studios to global headquarters, our teams combine
              local insight and world-class execution.
            </p>
          </div>
          <div className="MapSlider_map-slider__globe__1i3xE">
            <div className="GlobeMap_globe-map__hwpQX">
              <div className="GlobeMap_globe-map__pin__dqMMu">
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="87">
                  <g fill="none">
                    <path
                      fill="#DB0840"
                      stroke="#FFF"
                      strokeWidth="2"
                      d="M30 1C13.98 1 1 13.796 1 29.573c0 3.531.382 6.712 1.19 9.872 1.337 5.23 3.87 10.598 8.453 18.117 1.996 3.275 4.38 6.968 7.834 12.173 1.563 2.355 8.607 12.887 10.557 15.819.183.277.5.446.841.446.341 0 .658-.169.839-.442-1.432 2.184 15.466-23.33 18.471-28.191 4.665-7.543 7.188-12.719 8.549-17.764.862-3.195 1.266-6.408 1.266-10.03C59 13.796 46.02 1 30 1z"
                    ></path>
                    <path
                      fill="#FFF"
                      d="M51.476 36.329c-.895-.512-1.251-1.473-1.803-2.23-.196-.273-.538-.457-.72-.73-.643-1.705-1.188-3.41-1.363-5.353a5.575 5.575 0 0 0-.503-1.432c-.81-1.276-1.859-2.442-2.865-3.615l-.287-.286-3.543-2.865a7.134 7.134 0 0 0-1.419-.736 3.668 3.668 0 0 0-2.488.088c-.762.396-1.447.962-2.25 1.228-1.16.512-2.474.614-3.474 1.487-.587.49-1.076 1.261-1.943 1.132a3.76 3.76 0 0 0-2.564.893c-.657.58-1.28 1.269-2.132 1.432-.748.307-1.656.157-2.418.437-1.202.293-2.167 1.227-3.187 1.943-1.027.58-2.013 1.207-3.299 1.18a2.207 2.207 0 0 1-1.908-1.411 2.318 2.318 0 0 1 .755-2.449c.762-.886 1.971-1.411 2.565-2.434a1.853 1.853 0 0 0-.147-1.924c-.796-.797-1.929-.825-2.956-1.07-.657-.178-1.167-.669-1.747-.975-.21.327-.308.716-.273 1.104a2.662 2.662 0 0 0 1.734 2.067c.524.218 1.118.061 1.656.17a.51.51 0 0 1 .314.368.58.58 0 0 1-.174.396c-.839.682-1.72 1.337-2.418 2.182-.748.785-.832 2.169-.44 3.158a3.473 3.473 0 0 0 2.802 2.025c.852.137 1.607-.088 2.39-.252a.277.277 0 0 1 .189.164c.076.668-.028 1.261.125 1.875.14.518.322 1.023.539 1.514a.65.65 0 0 1-.14.546c-.42.293-.874.545-1.356.743l-1.16.068-.224.102c-.433.498-.447 1.01-.713 1.569-.356.573-1.013.852-1.565 1.187-.171.29-.383.557-.63.79-.195.15-.432.24-.684.246-.398.45-.49 1.078-.594 1.691.657.062 1.342.41 1.985.062.552-.252.67-.907 1.083-1.303.328-.348.825-.088 1.216-.013.58.027 1-.382 1.489-.573.342-.218.622-.6.978-.819.692-.32 1.642-.143 2.362-.013.56.136 1.14.068 1.657-.157.46-.28.894-.634 1.411-.75.462-.232.692-.744.88-1.16.155-.395.539-.702.511-1.159-.119-.095-.259-.32-.433-.3-.224.3-.392.62-.643.92-1.076 1.433-1.93 1.57-4.117 1.57a5.159 5.159 0 0 0-4.207 1.67.213.213 0 0 1-.237-.048c0-.204.062-.416.181-.593.993-1.384 2.551-1.705 4.194-1.739a6.46 6.46 0 0 0 2.501-.587 3.74 3.74 0 0 0 1.79-1.895c.188-.914.265-1.91.503-2.783a.36.36 0 0 1 .321-.123c.126.014.23.09.28.198-.168.9-.468 1.964-.105 2.837.713.92 2.04.423 3.012.375.937-.054 1.873-.04 2.796.048.482-.014.838.102 1.209-.191a.814.814 0 0 0 .112-.552c-.028-.266-.252-.423 0-.641a.342.342 0 0 1 .356.054c.14.362.196.75.433 1.078a5.7 5.7 0 0 0 1.838 1.398c.63.423 1.146 1.016 1.188 1.76.007.381-.349.64-.65.818a3.11 3.11 0 0 1-1.656.388c-.79-.375-1-1.186-1.523-1.786-.266-.294-.671-.341-1.07-.321a6.1 6.1 0 0 0-1.258.19.403.403 0 0 0-.119.356c.273.477.567.94.867 1.418.35.205.825-.116 1.118.246.259.388.42.832.811 1.125a1.44 1.44 0 0 0 1.433.266 18.639 18.639 0 0 0 3.235-1.228h.35c.412-.04.874-.02 1.167-.327.322-.443.608-.914.853-1.398.251-.334.14-.839.028-1.207a6.844 6.844 0 0 1-1.063-1.998c-.251-.069-.475-.219-.761-.205-.133.061-.322.034-.399.177l1.181 2.271c.098.205.14.437.12.662-.154.494-.435.94-.819 1.296-.07.04-.167.081-.223-.014.133-.498.37-1.037.342-1.603l-1.153-2.025c-.14-.273-.37-.525-.356-.846a.37.37 0 0 1 .251-.307c.503-.157 1.118-.136 1.503-.525.51-.77.615-1.527.748-2.448a.355.355 0 0 1 .237-.17.322.322 0 0 1 .238.143 3.707 3.707 0 0 1-.657 2.81.24.24 0 0 0 .049.306c.566.17 1.167 0 1.642-.252a9.2 9.2 0 0 0 1.726-.218.2.2 0 0 1 .126.143c-.36.389-.6.868-.692 1.384-.447.457-1.083.853-1.132 1.5.077.328.364.021.56.055a.156.156 0 0 1 .139.17l-.44.642c-.238.375.042.818.042 1.173.14.286.454.463.775.443.678-.027 1.272-.28 1.866-.484a4.149 4.149 0 0 1 2.202-.15c.699-.041 1.223-.498 1.747-.948.28.034.545.17.727.395a3.887 3.887 0 0 0 3.033 1.173l.098-.054a.168.168 0 0 0-.042-.26l-.699-.354c-.49-.307-1.104-.71-1.272-1.248a6.477 6.477 0 0 0-.713-1.473c-.105-.232-.35-.423-.447-.655a.112.112 0 0 1 .105-.061c.769.259 1.237 1.01 1.593 1.678.573.77 1.391 1.432 2.46 1.254l.091-.075a.14.14 0 0 0 0-.177l-.538-.26h-.007z"
                    ></path>
                  </g>
                </svg>
              </div>
              <div className="GlobeMap_globe-map__canvas__rjgoL GlobeMap_globe-map__canvas--is-draggable__IvWHB">
                <svg></svg>
              </div>
              <div className="GlobeMap_globe-map__shadow__lPcr_"></div>
            </div>
          </div>
          <div className="MapSliderLocationRail_map-slider-location-rail__gVRzn">
            <div className="MapSliderLocationRail_map-slider-location-rail__cards__X3hNf">
              {locationHighlights.map((location) => (
                <a
                  key={location.city}
                  className="MapSliderLocationRail_map-slider-location-rail__card__q2fMw"
                  href="/security-check"
                >
                  <span>{location.city}</span>
                  <h3>{location.area}</h3>
                  <strong>{location.openings}</strong>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section data-id="teaser" className="Teaser_teaser__nOzqs">
          <div className="Teaser_teaser__inner__WRHQc">
            <img
              loading="lazy"
              className="Image_image__3QFzw Teaser_teaser__image__dgLPe"
              alt="MS_211015_RBMH_HR_Elsbethen_1311"
              srcSet="https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/pgqctmnjdmvrfardnhoh/MS_211015_RBMH_HR_Elsbethen_1311 500w, https://img.redbull.com/images/w_650,q_auto,f_auto/rbjobs/staging/pgqctmnjdmvrfardnhoh/MS_211015_RBMH_HR_Elsbethen_1311 650w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/pgqctmnjdmvrfardnhoh/MS_211015_RBMH_HR_Elsbethen_1311 1000w, https://img.redbull.com/images/w_1300,q_auto,f_auto/rbjobs/staging/pgqctmnjdmvrfardnhoh/MS_211015_RBMH_HR_Elsbethen_1311 1300w"
              src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/pgqctmnjdmvrfardnhoh/MS_211015_RBMH_HR_Elsbethen_1311"
              sizes="50vw"
            />
            <div className="Teaser_teaser__heading-wrapper__HZ0vP">
              <span className="Teaser_teaser__kicker__kuNru">
                TALENT COMMUNITIES
              </span>
              <h3 className="Teaser_teaser__heading__O_6hs">
                Join our Talent Communities!
              </h3>
              <a
                className="Teaser_teaser__link__qhxLP"
                href="/security-check"
              >
                Join now
              </a>
            </div>
          </div>
        </section>

        <section data-id="job-listings" className="JobListings_job-listings__m2aZp">
          <div className="JobListings_job-listings__intro__r8JQ2">
            <span className="JobListings_job-listings__kicker__S1yq9">
              OPEN POSITIONS
            </span>
            <h2 className="JobListings_job-listings__heading__q7aLM">
              Social Media Marketing roles
            </h2>
            <p className="JobListings_job-listings__description__m3uQp">
              Explore SMM opportunities focused on Google marketing and Facebook
              marketing.
            </p>
            <div className="JobListings_job-listings__signal-row__Q9mNf">
              {hiringSignals.map((signal) => (
                <span key={signal}>{signal}</span>
              ))}
            </div>
            <div className="JobListings_job-listings__meta__E4pKt">
              <strong>{jobs.length} active roles</strong>
              <span>Updated weekly by global talent partners</span>
            </div>
          </div>
          <div className="JobListings_job-listings__grid__J3u6v">
            {jobs.map((job, index) => (
              <button
                key={job.title}
                className="JobListings_job-card__0j1nK JobListings_job-card--button__6q8Md"
                type="button"
                onClick={() => handleJobClick(job)}
              >
                <h3>{job.title}</h3>
                <p>{job.summary}</p>
                <div className="JobListings_job-card__meta__n5hKd">
                  <span>
                    {index % 3 === 0 ? "Full-time · Global" : "Full-time · Hybrid"}
                  </span>
                  <em>{index % 2 === 0 ? "Performance" : "Growth"}</em>
                </div>
                <b>View role details</b>
              </button>
            ))}
          </div>
        </section>

        <section
          data-id="moodboard-tiles"
          className="MoodboardTiles_moodboard-tiles__UuK8U"
        >
          <div className="MoodboardTiles_moodboard-tiles__intro__dfmhZ">
            <span className="MoodboardTiles_moodboard-tiles__intro-kicker__k4zgA">
              Red Bull
            </span>
            <h2 className="MoodboardTiles_moodboard-tiles__intro-heading__U4cHi">
              Giving wiiings to people and ideas since 1987
            </h2>
            <p className="MoodboardTiles_moodboard-tiles__intro-description__6whER">
              In the 1980s Dietrich Mateschitz developed a formula known as the
              Red Bull Energy Drink. This was not only the launch of a
              completely new product, in fact it was the birth of a totally new
              product category.
            </p>
            <a
              className="MoodboardTiles_moodboard-tiles__intro-link__tuhoC"
              href="/security-check"
            >
              The company beyond the can
            </a>
          </div>
          <div className="MoodboardGrid_moodboard-grid__B69Of">
            <div className="MoodboardGrid_moodboard-grid__grid__DgqOu">
              <div className="MoodboardGrid_moodboard-grid__tile__v_yhO">
                <a
                  className="Tile_tile__7iiKe Tile_is-horizontal__PSg08"
                  href="/security-check"
                >
                  <div className="Tile_tile__inner__dNcS_">
                    <img
                      loading="lazy"
                      className="Image_image__3QFzw Tile_tile__image__MxK2i"
                      alt="A man holding a Red Bull can who starts his career at Red Bull and in doing so works for a company that develops unique marketing concepts"
                      srcSet="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer 150w, https://img.redbull.com/images/w_350,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer 350w, https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer 500w, https://img.redbull.com/images/w_700,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer 700w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer 1000w"
                      src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/fzuceuz6xab0cowylpoh/red-bull-careers-product-pioneer"
                      sizes="(min-width: 1000px) 33vw, (min-width: 1200px) 490px, 50vw"
                    />
                    <span
                      className="Tile_tile__label__Telfm"
                      aria-hidden="true"
                    >
                      Energy Drinks
                    </span>
                  </div>
                </a>
              </div>
              <div className="MoodboardGrid_moodboard-grid__tile__v_yhO">
                <a
                  className="Tile_tile__7iiKe Tile_is-horizontal__PSg08"
                  href="/security-check"
                >
                  <div className="Tile_tile__inner__dNcS_">
                    <img
                      loading="lazy"
                      className="Image_image__3QFzw Tile_tile__image__MxK2i"
                      alt="Red Bull headquarter in Austria as one of the corporate locations for Red Bull jobs worldwide"
                      srcSet="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria 150w, https://img.redbull.com/images/w_350,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria 350w, https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria 500w, https://img.redbull.com/images/w_700,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria 700w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria 1000w"
                      src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/e63jgn8qovenkdobwgj7/red-bull-jobs-headquarter-austria"
                      sizes="(min-width: 1000px) 33vw, (min-width: 1200px) 490px, 50vw"
                    />
                    <span
                      className="Tile_tile__label__Telfm"
                      aria-hidden="true"
                    >
                      Company
                    </span>
                  </div>
                </a>
              </div>
              <div className="MoodboardGrid_moodboard-grid__tile__v_yhO">
                <a
                  className="Tile_tile__7iiKe Tile_is-vertical__OydYZ"
                  href="/security-check"
                >
                  <div className="Tile_tile__inner__dNcS_">
                    <img
                      loading="lazy"
                      className="Image_image__3QFzw Tile_tile__image__MxK2i"
                      alt="Red Bull athlete in action representing the brand and our ambassador community"
                      srcSet="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 150w, https://img.redbull.com/images/w_350,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 350w, https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 500w, https://img.redbull.com/images/w_700,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 700w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 1000w"
                      src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping"
                      sizes="(min-width: 1000px) 33vw, (min-width: 1200px) 490px, 50vw"
                    />
                    <span
                      className="Tile_tile__label__Telfm"
                      aria-hidden="true"
                    >
                      Athletes
                    </span>
                  </div>
                </a>
              </div>
              <div className="MoodboardGrid_moodboard-grid__tile__v_yhO">
                <a
                  className="Tile_tile__7iiKe Tile_is-horizontal__PSg08"
                  href="/security-check"
                >
                  <div className="Tile_tile__inner__dNcS_">
                    <img
                      loading="lazy"
                      className="Image_image__3QFzw Tile_tile__image__MxK2i"
                      alt="Red Bull athlete Neguin jumps up in the air on a pier by a lake at sunset while spreading his arms"
                      srcSet="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 150w, https://img.redbull.com/images/w_350,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 350w, https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 500w, https://img.redbull.com/images/w_700,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 700w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping 1000w"
                      src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/pn6k1byuasyhjptns9kl/red-bull-athlete-neguin-jumping"
                      sizes="(min-width: 1000px) 33vw, (min-width: 1200px) 490px, 50vw"
                    />
                    <span
                      className="Tile_tile__label__Telfm"
                      aria-hidden="true"
                    >
                      Media House
                    </span>
                  </div>
                </a>
              </div>
              <div className="MoodboardGrid_moodboard-grid__tile__v_yhO">
                <a
                  className="Tile_tile__7iiKe Tile_is-horizontal__PSg08"
                  href="/security-check"
                >
                  <div className="Tile_tile__inner__dNcS_">
                    <img
                      loading="lazy"
                      className="Image_image__3QFzw Tile_tile__image__MxK2i"
                      alt="culture-dancer.jpg"
                      srcSet="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename 150w, https://img.redbull.com/images/w_350,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename 350w, https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename 500w, https://img.redbull.com/images/w_700,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename 700w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename 1000w"
                      src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/oljtyhlumlzlyfilwcl4/filename"
                      sizes="(min-width: 1000px) 33vw, (min-width: 1200px) 490px, 50vw"
                    />
                    <span
                      className="Tile_tile__label__Telfm"
                      aria-hidden="true"
                    >
                      Culture
                    </span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section data-id="principles" className="Principles_principles__BwheA">
          <div className="Principles_principles__intro__W0yUZ">
            <span className="Principles_principles__kicker__JPdrj">
              What drives us
            </span>
            <blockquote className="Principles_principles__quote__40rHV">
              Chasing our potential
            </blockquote>
            <p className="Principles_principles__description__TicQX">
              Since the early days of Red Bull, an entrepreneurial mindset has
              always guided our approach to work and the environment we create:
            </p>
          </div>
          <div className="CardRail_card-rail__7SpVq">
            <div className="CardRail_card-rail__grid__h3VbQ">
              {principleCards.map((item) => (
                <article
                  key={item.title}
                  className="CardRail_card-rail__item__s6bLx"
                >
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          data-id="wingfinder-module"
          className="WingfinderModule_wingfinder-module__SbJBK"
        >
          <video
            className="WingfinderModule_wingfinder-module__background__Ufce9"
            muted
            loop
            preload="auto"
            playsInline
            autoPlay
            src="https://jobs.redbull.com/wingfindervideo.mp4"
          ></video>
          <div className="WingfinderModule_wingfinder-module__content-wrapper__JMdXC">
            <span className="WingfinderModule_wingfinder-module__kicker__wiufG">
              RED BULL WINGFINDER
            </span>
            <h2 className="WingfinderModule_wingfinder-module__title__reaWx">
              Discover your strengths
            </h2>
            <p className="WingfinderModule_wingfinder-module__content__V_WW_">
              Red Bull Wingfinder is an assessment that focuses on your
              strengths, and gives you the tools and coaching to be even better.
            </p>
            <div className="WingfinderModule_wingfinder-module__traits__q4fVm">
              {wingfinderTraits.map((trait) => (
                <span key={trait}>{trait}</span>
              ))}
            </div>
            <a
              className="WingfinderModule_wingfinder-module__linktext__wQwe5"
              href="/security-check"
            >
              Launch Wingfinder
            </a>
          </div>
        </section>

        <section data-id="worb-carousel" className="WorbCarousel_worb-carousel__uHkiU">
          <h2 className="WorbCarousel_worb-carousel__title__e_7G8">
            More from the world of Red Bull
          </h2>
          <div className="WorbCarousel_worb-carousel__intro__r8mTp">
            <p>
              Discover the teams, stories, and disciplines behind our global
              growth engine.
            </p>
            <div className="WorbCarousel_worb-carousel__tags__a2mDv">
              {worldTags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </div>
          <div className="WorbCarousel_worb-carousel__container__dFOTF">
            <div className="WorbCarousel_worb-carousel__navigation-overlay__eLL7f WorbCarousel_worb-carousel__navigation-overlay--left__hQh9E"></div>
            <div className="WorbCarousel_worb-carousel__navigation-overlay__eLL7f WorbCarousel_worb-carousel__navigation-overlay--right__5_iSF"></div>
            <div className="WorbCarousel_worb-carousel__timeline-wrapper__Zh8ni">
              <div className="WorbCarousel_worb-carousel__timeline__14I83"></div>
            </div>
            <div className="WorbCarousel_worb-carousel__cards__C7b1n">
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Company</span>
                <h3>Giving wiiings to people and ideas since 1987</h3>
                <p>
                  Get to know the brand story, the people behind it, and the
                  entrepreneurial mindset that shapes our work.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Brand story</span>
                  <span>Global teams</span>
                </div>
                <a href="/security-check">Learn more →</a>
              </div>
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Athletes</span>
                <h3>Meet the athletes who push boundaries</h3>
                <p>
                  Discover the people and stories that define Red Bull sports,
                  innovation, and community impact.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Profiles</span>
                  <span>Highlights</span>
                </div>
                <a href="/security-check">View athletes →</a>
              </div>
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Culture</span>
                <h3>Urban culture, music, and creativity</h3>
                <p>
                  Explore stories across music, art, and events that inspire the
                  next generation of creators.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Stories</span>
                  <span>Events</span>
                </div>
                <a href="/security-check">Explore culture →</a>
              </div>
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Media House</span>
                <h3>World-class content, produced globally</h3>
                <p>
                  Learn how Red Bull Media House tells powerful stories across
                  sport, music, and culture.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Production</span>
                  <span>Storytelling</span>
                </div>
                <a href="/security-check">See the work →</a>
              </div>
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Innovation</span>
                <h3>From product to experience design</h3>
                <p>
                  See how we think about innovation, experimentation, and the
                  ideas that power our growth.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Ideas</span>
                  <span>Innovation</span>
                </div>
                <a href="/security-check">Discover more →</a>
              </div>
              <div className="WorbCarousel_worb-carousel__card__3h4Tw">
                <span>Careers</span>
                <h3>Find where you can make your mark</h3>
                <p>
                  Join teams that move fast, think boldly, and support your
                  growth every step of the way.
                </p>
                <div className="WorbCarousel_worb-carousel__meta__gqP8z">
                  <span>Teams</span>
                  <span>Benefits</span>
                </div>
                <a href="/security-check">Explore roles →</a>
              </div>
            </div>
          </div>
        </section>

        <section data-id="teaser" className="Teaser_teaser__nOzqs">
          <div className="Teaser_teaser__inner__WRHQc">
            <img
              loading="lazy"
              className="Image_image__3QFzw Teaser_teaser__image__dgLPe"
              alt="Red Bull athlete Sasha DiGiulian with headband is smiling"
              srcSet="https://img.redbull.com/images/w_500,q_auto,f_auto/rbjobs/staging/AP-1SUYUF4H11W11_hires_jpeg_24bit_rgb/sasha-digiulian-red-bull-athlete-smiling 500w, https://img.redbull.com/images/w_650,q_auto,f_auto/rbjobs/staging/AP-1SUYUF4H11W11_hires_jpeg_24bit_rgb/sasha-digiulian-red-bull-athlete-smiling 650w, https://img.redbull.com/images/w_1000,q_auto,f_auto/rbjobs/staging/AP-1SUYUF4H11W11_hires_jpeg_24bit_rgb/sasha-digiulian-red-bull-athlete-smiling 1000w, https://img.redbull.com/images/w_1300,q_auto,f_auto/rbjobs/staging/AP-1SUYUF4H11W11_hires_jpeg_24bit_rgb/sasha-digiulian-red-bull-athlete-smiling 1300w"
              src="https://img.redbull.com/images/w_150,q_auto,f_auto/rbjobs/staging/AP-1SUYUF4H11W11_hires_jpeg_24bit_rgb/sasha-digiulian-red-bull-athlete-smiling"
              sizes="50vw"
            />
            <div className="Teaser_teaser__heading-wrapper__HZ0vP">
              <span className="Teaser_teaser__kicker__kuNru">Red Bull Jobs</span>
              <h3 className="Teaser_teaser__heading__O_6hs">
                Got questions? We got you covered.
              </h3>
              <a className="Teaser_teaser__link__qhxLP" href="/security-check">
                Jobs FAQ
              </a>
            </div>
          </div>
        </section>

        <footer className="rb-footer">
          <div className="rb-footer__content">
            <div className="rb-footer__brand">
              <h3>Red Bull Jobs</h3>
              <p>Your journey starts here.</p>
              <p>More than 21,000 people in over 175 countries.</p>
            </div>
            <div className="rb-footer__columns">
              <div>
                <h4>Explore</h4>
                <a href="/security-check">Open positions</a>
                <a href="/security-check">Talent community</a>
                <a href="/security-check">Locations</a>
              </div>
              <div>
                <h4>Resources</h4>
                <a href="/security-check">Jobs FAQ</a>
                <a href="/security-check">Hiring process</a>
                <a href="/security-check">Early careers</a>
              </div>
              <div>
                <h4>Stay connected</h4>
                <a href="/security-check">Instagram</a>
                <a href="/security-check">LinkedIn</a>
                <a href="/security-check">YouTube</a>
              </div>
            </div>
          </div>
          <div className="rb-footer__bottom">
            <span>© 2026 Red Bull Jobs</span>
            <div className="rb-footer__bottom-links">
              <a href="/privacy-policy">Privacy</a>
              <a href="/terms-of-use">Terms</a>
              <a href="/security-check">Contact</a>
            </div>
          </div>
        </footer>
      </div>
      {activeJob && (
        <div className="JobModal_job-modal__M5xQ2" role="dialog" aria-modal="true">
          <div className="JobModal_job-modal__backdrop__5nZtQ" onClick={closeModal}></div>
          <div className="JobModal_job-modal__content__S2dKb">
            <button
              className="JobModal_job-modal__close__t9qMd"
              type="button"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
            <h2>{activeJob.title}</h2>
            <p className="JobModal_job-modal__summary__y3qVf">{activeJob.summary}</p>
            <p className="JobModal_job-modal__description__T1w9c">
              {activeJob.description}
            </p>
            <div className="JobModal_job-modal__section__m2kX1">
              <h3>Responsibilities</h3>
              <ul>
                {activeJob.responsibilities.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="JobModal_job-modal__section__m2kX1">
              <h3>Requirements</h3>
              <ul>
                {activeJob.requirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="JobModal_job-modal__actions__r4HkA">
              <button
                type="button"
                className="JobModal_job-modal__apply__nP9wB"
                onClick={handleApply}
              >
                Apply now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
